import { useContext, useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import * as R from 'ramda';

import UserGroupIcon from '@atom/components/common/UserGroupIcon';
import EditTaskUsers from '@atom/components/common/workOrderDetail/editTaskUserGroupModal/EditTaskUsers';
import WorkTemplateContext from '@atom/components/workTemplate/WorkTemplateContext';
import { GET_USERS } from '@atom/graph/user';
import { GET_USER_GROUP } from '@atom/graph/userGroup';
import { WORK_TEMPLATE_TASK_USER_GROUP_DELETE } from '@atom/graph/workTemplate';
import { useNavigation } from '@atom/hooks/useNavigation';
import { Button, Icon, IconButton, Modal } from '@atom/mui';
import {
  UserDetail,
  UsersConnection,
  UsersConnectionInput,
} from '@atom/types/user';
import { UserGroup } from '@atom/types/userGroups';
import {
  InheritedComponentType,
  WorkOrderTemplateUserGroupDeleteInput,
  WorkTemplateTaskUserGroup,
} from '@atom/types/workTemplate';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { pluralize } from '@atom/utilities/stringUtilities';
import { isComponentInherited } from '@atom/utilities/workTemplateUtilities';

import classes from './teamSection.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
  taskUserGroup: WorkTemplateTaskUserGroup;
}

const PAGE_LIMIT = 25;

const EditTaskUserGroupModal = ({ open, onClose, taskUserGroup }: Props) => {
  const [users, setUsers] = useState<UserDetail[]>([]);
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [, { Link }] = useNavigation();

  const { workTemplate, task, refetch } = useContext(WorkTemplateContext);

  const [getUserGroup, { data, error, loading }] = useLazyQuery<{
    userGroup: UserGroup;
  }>(GET_USER_GROUP, {
    fetchPolicy: 'no-cache',
  });

  const [getUsers, { data: usersData, loading: usersLoading }] = useLazyQuery<
    { users: UsersConnection },
    { input: UsersConnectionInput }
  >(GET_USERS, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    const nextUsers = R.pathOr([], ['users', 'users'], usersData);

    setUsers([...users, ...nextUsers]);
    setTotal(usersData?.users?.totalCount || 0);
  }, [usersData]);

  const [taskUserGroupRemove] = useMutation<
    { workOrderTemplateUserGroupDelete: boolean },
    { input: WorkOrderTemplateUserGroupDeleteInput }
  >(WORK_TEMPLATE_TASK_USER_GROUP_DELETE, {
    onCompleted: () => {
      refetch();
    },
  });

  useEffect(() => {
    if (open) {
      getUserGroup({
        variables: {
          id: taskUserGroup.userGroupId,
        },
      });
    }
  }, [open, getUserGroup]);

  useEffect(() => {
    if (open) {
      getUsers({
        variables: {
          input: {
            showAdmin: hasRolePermissions(ROLE_SETS.ADMIN),
            userGroupId: taskUserGroup.userGroupId,
            limit: PAGE_LIMIT,
            page,
          },
        },
      });
    } else {
      setUsers([]);
      setPage(1);
    }
  }, [open, page]);

  const removeUserGroup = async () => {
    await taskUserGroupRemove({
      variables: {
        input: {
          taskId: task.id,
          workTemplateId: workTemplate.id,
          userGroupId: taskUserGroup.userGroupId,
        },
      },
    });

    refetch();
    onClose();
  };

  const userGroup = data?.userGroup;
  const isLoading = loading || error || R.isNil(userGroup);
  const fullGroupPath = [
    'Directory',
    ...R.pathOr([], ['groupPath'], userGroup),
  ];

  const isItemInherited = isComponentInherited(
    InheritedComponentType.TASK_USER_GROUP,
    task?.inheritedComponents || [],
    taskUserGroup?.userGroupId,
  );

  const pathIds = userGroup
    ? [...userGroup.ancestors.map(({ id }) => id), userGroup.id]
    : [];

  const modalTitle = (
    <div className={classes.headerContainer}>
      <div className={classes.headerLeft}>
        {isLoading ? (
          <div />
        ) : (
          <>
            <div>
              <UserGroupIcon colorId={userGroup.colorId} />
            </div>
            <div className={classes.titleContainer}>
              <div className={classes.headerTitle}>{userGroup.name}</div>
              <div className={classes.headerPath}>
                {total} {pluralize(total, 'User')}
                <span className={classes.spacer}>•</span>
                {fullGroupPath.join(' / ')}
              </div>
            </div>
          </>
        )}
      </div>
      {!isLoading && (
        <Link
          to={`/team?activeGroupId=${userGroup?.id}&activePathIds=${pathIds}`}
        >
          <IconButton>
            <Icon>launch</Icon>
          </IconButton>
        </Link>
      )}
    </div>
  );

  const handlePageScroll = () => {
    setPage(page + 1);
  };

  const actionDisabled = !!loading || isItemInherited;

  const renderFooter = (
    <div className={classes.modalFooter}>
      <div>
        <Button onClick={removeUserGroup} disabled={actionDisabled}>
          Remove From Task
        </Button>
      </div>
      <div>
        <Button onClick={onClose}>Cancel</Button>
      </div>
    </div>
  );

  return (
    <Modal
      title={modalTitle}
      open={open}
      footer={renderFooter}
      loading={loading}
    >
      <EditTaskUsers
        loading={usersLoading}
        users={users}
        total={total}
        handlePageScroll={handlePageScroll}
      />
    </Modal>
  );
};

export default EditTaskUserGroupModal;
